import './UserMenu.scss';
import classNames from 'classnames';
import { MouseEvent, useRef, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { selectUser } from 'store/slices/auth';
import { IoIosPerson, IoIosLogOut, IoIosPeople } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import {
  ADMINISTRATION,
  AUTHOR,
  // REVIEWER,
  ROLES_MAPPING,
} from 'constants/roles';
import { useQueryClient } from '@tanstack/react-query';
import { signOutUser } from 'helpers/signOut';

/**
 * @description UserMenu component displays the user dropdown in the header.
 * Contains user information and navigation options like profile, manage users, and logout with role-based visibility.
 *
 * @SADtrace SAD-003-1.1, SAD-003-2.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} The rendered UserMenu component
 */
const UserMenu = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const ddEl = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  /**
   * @description Signs the user out and navigates to login page.
   * Clears user session and query cache before redirecting.
   *
   * @SADtrace SAD-003-1.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {MouseEvent} e - The click event
   *
   * @returns {Promise<void>}
   */
  const handleSignOut = async (e: MouseEvent) => {
    e.preventDefault();
    await signOutUser(dispatch, queryClient);
    navigate('/login');
  };

  /**
   * @description Closes the dropdown when clicking outside of it
   *
   * @SADtrace SAD-003-1.1, SAD-003-2.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Event} event - The click event
   *
   * @returns {void}
   */
  const handleHtmlClick = (event: Event) => {
    // @ts-ignore
    if (!!ddEl && !ddEl.current.contains(event.target as any)) {
      setIsOpen(false);
    }
  };

  /**
   * @description Toggles the dropdown open/closed state
   *
   * @SADtrace SAD-003-1.1, SAD-003-2.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {MouseEvent} event - The click event
   *
   * @returns {void}
   */
  const handleToggle = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(!isOpen);
  };

  /**
   * @description Gets the display name for the user.
   * Returns first and last name if available, otherwise email.
   *
   * @SADtrace SAD-003-1.1, SAD-003-2.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @returns {string} The formatted username
   */
  const getUsername = (): string => {
    if (user?.first_name && user.last_name) {
      return user.first_name + ' ' + user.last_name;
    }

    // v8 ignore start -- WAIVER(MICRO): every authenticated user record carries an email; the '' fallback exists only to satisfy the optional user type
    return user?.email || '';
    // v8 ignore stop -- WAIVER(MICRO)
  };
  const username = useMemo(getUsername, [user]);

  /**
   * @description Handles the initial load of the component
   */
  useEffect(() => {
    const htmlEl: HTMLElement | null = document.querySelector('html');

    // v8 ignore start -- WAIVER(DEFENSIVE): document.querySelector('html') always finds the document root element, so the null arm is an unreachable fail-safe
    if (htmlEl) {
      // v8 ignore stop -- WAIVER(DEFENSIVE)
      htmlEl.addEventListener('click', handleHtmlClick);
    }

    return () => {
      // v8 ignore start -- WAIVER(DEFENSIVE): document.querySelector('html') always finds the document root element, so the null arm is an unreachable fail-safe
      if (htmlEl) {
        // v8 ignore stop -- WAIVER(DEFENSIVE)
        htmlEl.removeEventListener('click', handleHtmlClick);
      }
    };
  }, []);

  const classnames = classNames({
    'dropdown top-navbar__user-dropdown': true,
    open: isOpen,
  });

  return (
    <div className={classnames} ref={ddEl} data-testid="user-menu">
      <a
        href="/#"
        className="top-navbar__user-dropdown-link"
        onClick={handleToggle}
        data-testid="user-menu-toggle"
      >
        <span>{username}</span>
        <span>
          <span className="top-navbar__user-dropdown-icon">
            <IoIosPerson size={40} stroke={'green'} />
          </span>
        </span>
        {/* <i className="dl-user-6"></i> */}
      </a>
      <div className="top-navbar__user-dropdown-menu">
        <div className="top-navbar__user-dropdown-menu__header">
          <div className="top-navbar__user-dropdown-menu__header-wrapper">
            {/* <div className="top-navbar__user-dropdown-menu__header-title">
              Aleksandar&nbsp;Dukleski
            </div> */}
            <div className="top-navbar__user-dropdown-menu__header-subtitle">
              {t('user_menu.access_level')}:&nbsp;
              <b>{t(ROLES_MAPPING[user?.role as string])}</b>
            </div>
          </div>
        </div>
        <ul className="top-navbar__user-dropdown__list">
          {/* v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type */}
          {[ADMINISTRATION, AUTHOR].includes(user?.role || '') ? (
            <li>
              {/* v8 ignore stop -- WAIVER(MICRO) */}
              <Link
                className="top-navbar__user-dropdown__link"
                to="/settings/profile"
                onClick={() => setIsOpen(false)}
              >
                <IoIosPerson className="top-navbar__user-dropdown__link-icon" />
                <span className="top-navbar__user-dropdown__link-text">
                  {t('user_menu.profile')}
                </span>
              </Link>
            </li>
          ) : null}

          {/* v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type */}
          {[ADMINISTRATION].includes(user?.role || '') ? (
            <li>
              {/* v8 ignore stop -- WAIVER(MICRO) */}
              <Link
                className="top-navbar__user-dropdown__link"
                to="/settings/users"
                onClick={() => setIsOpen(false)}
              >
                <IoIosPeople className="top-navbar__user-dropdown__link-icon" />
                <span className="top-navbar__user-dropdown__link-text">
                  {t('user_menu.manage_users')}
                </span>
              </Link>
            </li>
          ) : null}

          {/* v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type */}
          {[ADMINISTRATION, AUTHOR].includes(user?.role || '') ? (
            <li className="divider"></li>
          ) : null}
          {/* v8 ignore stop -- WAIVER(MICRO) */}

          <li>
            <a
              href="/#"
              className="top-navbar__user-dropdown__link"
              onClick={handleSignOut}
              data-testid="logout-button"
            >
              <IoIosLogOut className="top-navbar__user-dropdown__link-icon" />
              <span className="top-navbar__user-dropdown__link-text">
                {t('user_menu.logout')}
              </span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default UserMenu;
